import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { apiService, WatermarkSettings, WatermarkTextConfig } from '../services/api';
import { EyeIcon, PrinterIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { usePageTitle } from '../hooks/usePageTitle';
import { useUnsavedChanges } from '../contexts/UnsavedChangesContext';
import AlignmentControl from '../components/Watermark/AlignmentControl';
import WatermarkPreview from '../components/Watermark/WatermarkPreview';

// Helper function to calculate text from text_config with actual user/company data
// For recipient fields, we use placeholders as they're only available at runtime
function calculateWatermarkText(
  textConfig: WatermarkTextConfig,
  userEmail?: string,
  companyName?: string,
  deviceName?: string
): string {
  // If custom text is enabled, return only the custom text value
  if (textConfig.custom_text && textConfig.custom_text_value) {
    return textConfig.custom_text_value;
  }

  // Build text from enabled options with actual values where available
  const parts: string[] = [];

  if (textConfig.owner_company_name && companyName) {
    parts.push(companyName);
  }
  if (textConfig.recipient_company_name) {
    parts.push('{recipient_company_name}'); // Only available at runtime
  }
  if (textConfig.owner_email && userEmail) {
    parts.push(userEmail);
  }
  if (textConfig.recipient_email) {
    parts.push('{recipient_email}'); // Only available at runtime
  }
  if (textConfig.recipient_ip_address) {
    parts.push('{recipient_ip_address}'); // Only available at runtime
  }
  if (textConfig.timestamp) {
    // Use current timestamp
    const now = new Date();
    const timestamp = now.toLocaleString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
    parts.push(timestamp);
  }
  if (textConfig.file_name) {
    parts.push('{file_name}'); // Only available at runtime
  }
  if (textConfig.device_name && deviceName) {
    parts.push(deviceName);
  }

  return parts.join(' | ');
}

const AdminWatermarkSettings: React.FC = () => {
  const { t } = useTranslation('adminWatermarks');
  usePageTitle(t('title'));
  const { user } = useAuth();
  const { setHasUnsavedChanges, setOnSave, setIsSubmitting } = useUnsavedChanges();
  const [companyName, setCompanyName] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'screen' | 'print'>('screen');
  const [showPreview, setShowPreview] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);


  // Screen watermark state
  const [screenWatermark, setScreenWatermark] = useState<WatermarkSettings>({
    text_config: {
      owner_company_name: false,
      recipient_company_name: false,
      owner_email: false,
      recipient_email: false,
      recipient_ip_address: false,
      timestamp: false,
      file_name: false,
      device_name: false,
      custom_text: false,
      custom_text_value: '',
    },
    font: 'Arial',
    font_size: 12,
    color: '#000000',
    opacity: 0.5,
    angle: 45, // Default to diagonal (45°)
  });

  // Print watermark state
  const [printWatermark, setPrintWatermark] = useState<WatermarkSettings>({
    text_config: {
      owner_company_name: false,
      recipient_company_name: false,
      owner_email: false,
      recipient_email: false,
      recipient_ip_address: false,
      timestamp: false,
      file_name: false,
      device_name: false,
      custom_text: false,
      custom_text_value: '',
    },
    font: 'Arial',
    font_size: 12,
    color: '#000000',
    opacity: 0.5,
    angle: 45, // Default to diagonal (45°)
  });

  // Override toggles
  const [overrideScreenWatermark, setOverrideScreenWatermark] = useState(false);
  const [overridePrintWatermark, setOverridePrintWatermark] = useState(false);

  useEffect(() => {
    const loadData = async () => {
      if (!user?.company_id) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        const companySettings = await apiService.getCompanySecuritySettings(user.company_id);

        // Set watermark values from company settings
        if (companySettings.screen_watermark_settings) {
          const settings = companySettings.screen_watermark_settings;
          // Handle backward compatibility - if text_config doesn't exist, create default
          if (!settings.text_config) {
            settings.text_config = {
              owner_company_name: false,
              recipient_company_name: false,
              owner_email: false,
              recipient_email: false,
              recipient_ip_address: false,
              timestamp: false,
              file_name: false,
              device_name: false,
              custom_text: false,
              custom_text_value: '',
            };
          }
          // Normalize angle to 45° or -45° (diagonal only)
          if (settings.angle === undefined) {
            // Check if old alignment field exists
            if ((settings as any).alignment !== undefined) {
              const oldAlign = (settings as any).alignment;
              if (typeof oldAlign === 'number') {
                // Convert old alignment to new angle format (45 or -45)
                if (oldAlign === 0) {
                  settings.angle = 45; // Default to diagonal
                } else if (oldAlign === 90) {
                  settings.angle = -45; // Convert vertical to negative diagonal
              } else {
                  settings.angle = oldAlign === -45 ? -45 : 45; // Keep -45, otherwise default to 45
              }
            } else {
                settings.angle = 45; // Default to diagonal
              }
            } else {
              settings.angle = 45; // Default to diagonal
            }
          } else if (typeof settings.angle !== 'number') {
            settings.angle = 45;
          } else {
            // Normalize angle to only 45 or -45
            if (settings.angle !== 45 && settings.angle !== -45) {
              settings.angle = 45; // Default to diagonal if not valid
            }
          }
          setScreenWatermark(settings);
        }
        if (companySettings.print_watermark_settings) {
          const settings = companySettings.print_watermark_settings;
          // Handle backward compatibility - if text_config doesn't exist, create default
          if (!settings.text_config) {
            settings.text_config = {
              owner_company_name: false,
              recipient_company_name: false,
              owner_email: false,
              recipient_email: false,
              recipient_ip_address: false,
              timestamp: false,
              file_name: false,
              device_name: false,
              custom_text: false,
              custom_text_value: '',
            };
          }
          // Normalize angle to 45° or -45° (diagonal only)
          if (settings.angle === undefined) {
            // Check if old alignment field exists
            if ((settings as any).alignment !== undefined) {
              const oldAlign = (settings as any).alignment;
              if (typeof oldAlign === 'number') {
                // Convert old alignment to new angle format (45 or -45)
                if (oldAlign === 0) {
                  settings.angle = 45; // Default to diagonal
                } else if (oldAlign === 90) {
                  settings.angle = -45; // Convert vertical to negative diagonal
              } else {
                  settings.angle = oldAlign === -45 ? -45 : 45; // Keep -45, otherwise default to 45
              }
            } else {
                settings.angle = 45; // Default to diagonal
              }
            } else {
              settings.angle = 45; // Default to diagonal
            }
          } else if (typeof settings.angle !== 'number') {
            settings.angle = 45;
          } else {
            // Normalize angle to only 45 or -45
            if (settings.angle !== 45 && settings.angle !== -45) {
              settings.angle = 45; // Default to diagonal if not valid
            }
          }
          setPrintWatermark(settings);
        }

        // Set override toggles
        setOverrideScreenWatermark(companySettings.override_screen_watermark_settings || false);
        setOverridePrintWatermark(companySettings.override_print_watermark_settings || false);

        // Fetch company name for watermark text calculation
        if (user?.company_id) {
          try {
            const companyResponse = await apiService.get(`/companies/${user.company_id}`);
            setCompanyName(companyResponse.data.name || null);
          } catch (err) {
            console.error('Failed to load company name:', err);
          }
        }
      } catch (err: any) {
        console.error('Failed to load watermark settings:', err);
        setError(t('errors.loadFailed'));
        toast.error(t('toast.loadFailed'));
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [user, t]);

  // Use refs to store the latest watermark state so save handlers don't need them in dependencies
  const screenWatermarkRef = useRef(screenWatermark);
  const printWatermarkRef = useRef(printWatermark);
  const overrideScreenWatermarkRef = useRef(overrideScreenWatermark);
  const overridePrintWatermarkRef = useRef(overridePrintWatermark);

  useEffect(() => {
    screenWatermarkRef.current = screenWatermark;
  }, [screenWatermark]);

  useEffect(() => {
    printWatermarkRef.current = printWatermark;
  }, [printWatermark]);

  useEffect(() => {
    overrideScreenWatermarkRef.current = overrideScreenWatermark;
  }, [overrideScreenWatermark]);

  useEffect(() => {
    overridePrintWatermarkRef.current = overridePrintWatermark;
  }, [overridePrintWatermark]);

  // Track if we're currently saving to prevent auto-saves
  const isSavingRef = useRef(false);

  const handleSaveScreenWatermark = useCallback(async () => {
    if (!user?.company_id || isSavingRef.current) return;

    try {
      isSavingRef.current = true;
      setSaving(true);
      setIsSubmitting(true);
      setError(null);

      // Prepare text_config - ensure custom_text and combination are mutually exclusive
      const textConfig = { ...screenWatermarkRef.current.text_config };
      
      // If custom_text is true, clear all other options
      if (textConfig.custom_text) {
        textConfig.owner_company_name = false;
        textConfig.recipient_company_name = false;
        textConfig.owner_email = false;
        textConfig.recipient_email = false;
        textConfig.recipient_ip_address = false;
        textConfig.timestamp = false;
        textConfig.file_name = false;
        textConfig.device_name = false;
      } else {
        // If combination mode, clear custom_text_value
        textConfig.custom_text_value = '';
      }

      // Calculate text from text_config with actual user/company data
      const calculatedText = calculateWatermarkText(
        textConfig,
        user?.email,
        companyName || undefined,
        user?.device_name
      );

      const settingsToSave = {
        ...screenWatermarkRef.current,
        text: calculatedText,
        text_config: textConfig,
        angle: screenWatermarkRef.current.angle === -45 ? -45 : 45, // Normalize to 45 or -45
      };
      await apiService.updateCompanySecuritySettings(
        user.company_id,
        {
          screen_watermark_settings: settingsToSave,
          override_screen_watermark_settings: overrideScreenWatermarkRef.current,
        },
        false
      );

      setHasChanges(false);
      toast.success(t('toast.screenSaved'));
    } catch (err: any) {
      console.error('Failed to save screen watermark settings:', err);
      setError(t('errors.saveScreenFailed'));
      toast.error(err.response?.data?.error || t('toast.saveScreenFailed'));
    } finally {
      setSaving(false);
      setIsSubmitting(false);
      isSavingRef.current = false;
    }
  }, [user?.company_id, companyName, user?.email, user?.device_name, setSaving, setIsSubmitting, setError, setHasChanges, t]);

  const handleSavePrintWatermark = useCallback(async () => {
    if (!user?.company_id || isSavingRef.current) return;

    try {
      isSavingRef.current = true;
      setSaving(true);
      setIsSubmitting(true);
      setError(null);

      // Prepare text_config - ensure custom_text and combination are mutually exclusive
      const textConfig = { ...printWatermarkRef.current.text_config };
      
      // If custom_text is true, clear all other options
      if (textConfig.custom_text) {
        textConfig.owner_company_name = false;
        textConfig.recipient_company_name = false;
        textConfig.owner_email = false;
        textConfig.recipient_email = false;
        textConfig.recipient_ip_address = false;
        textConfig.timestamp = false;
        textConfig.file_name = false;
        textConfig.device_name = false;
      } else {
        // If combination mode, clear custom_text_value
        textConfig.custom_text_value = '';
      }

      // Calculate text from text_config with actual user/company data
      const calculatedText = calculateWatermarkText(
        textConfig,
        user?.email,
        companyName || undefined,
        user?.device_name
      );

      const settingsToSave = {
        ...printWatermarkRef.current,
        text: calculatedText,
        text_config: textConfig,
        angle: printWatermarkRef.current.angle === -45 ? -45 : 45, // Normalize to 45 or -45
      };
      await apiService.updateCompanySecuritySettings(
        user.company_id,
        {
          print_watermark_settings: settingsToSave,
          override_print_watermark_settings: overridePrintWatermarkRef.current,
        },
        false
      );

      setHasChanges(false);
      toast.success(t('toast.printSaved'));
    } catch (err: any) {
      console.error('Failed to save print watermark settings:', err);
      setError(t('errors.savePrintFailed'));
      toast.error(err.response?.data?.error || t('toast.savePrintFailed'));
    } finally {
      setSaving(false);
      setIsSubmitting(false);
      isSavingRef.current = false;
    }
  }, [user?.company_id, companyName, user?.email, user?.device_name, setSaving, setIsSubmitting, setError, setHasChanges, t]);

  // Handle watermark changes from preview
  const handleWatermarkChange = (updatedWatermark: WatermarkSettings) => {
    if (activeTab === 'screen') {
      setScreenWatermark(updatedWatermark);
    } else {
      setPrintWatermark(updatedWatermark);
    }
    setHasChanges(true);
  };

  // Use refs to store the latest save handlers to avoid unnecessary re-renders
  const handleSaveScreenWatermarkRef = useRef(handleSaveScreenWatermark);
  const handleSavePrintWatermarkRef = useRef(handleSavePrintWatermark);

  useEffect(() => {
    handleSaveScreenWatermarkRef.current = handleSaveScreenWatermark;
  }, [handleSaveScreenWatermark]);

  useEffect(() => {
    handleSavePrintWatermarkRef.current = handleSavePrintWatermark;
  }, [handleSavePrintWatermark]);

  // Set up unsaved changes tracking
  // IMPORTANT: This only sets the onSave function, it does NOT call it
  // The save will only happen when the user explicitly clicks the save button in the header
  // Using double arrow function pattern to prevent React from calling it immediately
  useEffect(() => {
    if (hasChanges) {
      setHasUnsavedChanges(true);
      // Use double arrow function pattern like UserDetails.tsx to prevent auto-calling
      setOnSave(() => () => {
        // This function is ONLY called from the header save button onClick handler
        if (activeTab === 'screen') {
          handleSaveScreenWatermarkRef.current();
        } else {
          handleSavePrintWatermarkRef.current();
        }
      });
    } else {
      setHasUnsavedChanges(false);
      setOnSave(null);
    }

    return () => {
      setOnSave(null);
    };
  }, [hasChanges, activeTab, setHasUnsavedChanges, setOnSave]);

  // Listen for preview event from header
  useEffect(() => {
    const handlePreview = () => {
      setShowPreview(true);
    };
    const handleClosePreview = () => {
      setShowPreview(false);
    };
    const handleSave = () => {
      if (activeTab === 'screen') {
        handleSaveScreenWatermark();
      } else {
        handleSavePrintWatermark();
      }
    };
    window.addEventListener('open-watermark-preview', handlePreview);
    window.addEventListener('close-watermark-preview', handleClosePreview);
    window.addEventListener('watermark-preview-save', handleSave);
    return () => {
      window.removeEventListener('open-watermark-preview', handlePreview);
      window.removeEventListener('close-watermark-preview', handleClosePreview);
      window.removeEventListener('watermark-preview-save', handleSave);
    };
  }, [activeTab, handleSaveScreenWatermark, handleSavePrintWatermark]);

  const handleToggleOverride = async (type: 'screen' | 'print', enabled: boolean) => {
    if (!user?.company_id) return;

    try {
      setSaving(true);
      setError(null);

      const updateData: any = {};
      if (type === 'screen') {
        updateData.override_screen_watermark_settings = enabled;
        setOverrideScreenWatermark(enabled);
      } else {
        updateData.override_print_watermark_settings = enabled;
        setOverridePrintWatermark(enabled);
      }

      await apiService.updateCompanySecuritySettings(user.company_id, updateData, false);

      const typeLabel = type === 'screen' ? t('override.screen') : t('override.print');
      toast.success(enabled ? t('toast.overrideEnabled', { type: typeLabel }) : t('toast.overrideDisabled', { type: typeLabel }));
    } catch (err: any) {
      console.error('Failed to toggle override:', err);
      setError(t('errors.overrideFailed'));
      toast.error(err.response?.data?.error || t('toast.overrideFailed'));
    } finally {
      setSaving(false);
    }
  };


  // Handle other text config checkboxes - if custom_text is enabled, disable it
  const handleTextConfigChange = (type: 'screen' | 'print', field: string, value: boolean) => {
    if (type === 'screen') {
      const newConfig = { ...screenWatermark.text_config, [field]: value };
      // If enabling any other option, disable custom_text and clear custom_text_value
      if (value && field !== 'custom_text') {
        newConfig.custom_text = false;
        newConfig.custom_text_value = '';
      }
      setScreenWatermark({
        ...screenWatermark,
        text_config: newConfig,
      });
      setHasChanges(true);
    } else {
      const newConfig = { ...printWatermark.text_config, [field]: value };
      // If enabling any other option, disable custom_text and clear custom_text_value
      if (value && field !== 'custom_text') {
        newConfig.custom_text = false;
        newConfig.custom_text_value = '';
      }
      setPrintWatermark({
        ...printWatermark,
        text_config: newConfig,
      });
      setHasChanges(true);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-governate-500"></div>
      </div>
    );
  }

  if (!user?.company_id) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">{t('errors.noCompany')}</p>
      </div>
    );
  }

  // Render watermark form fields
  const renderWatermarkForm = (type: 'screen' | 'print') => {
    const watermark = type === 'screen' ? screenWatermark : printWatermark;
    const overrideEnabled = type === 'screen' ? overrideScreenWatermark : overridePrintWatermark;

    return (
      <div className="space-y-4">
        {/* Override Toggle */}
        <div className="mb-6 pb-6 border-b border-gray-200 dark:border-gray-800">
          <div className="flex items-center justify-between">
            <div>
              <dt className="text-sm font-medium text-gray-700 dark:text-gray-300">
                {t('override.label', { type: type === 'screen' ? t('override.screen') : t('override.print') })}
              </dt>
              <dd className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                {t('override.description')}
              </dd>
            </div>
            <button
              type="button"
              onClick={() => handleToggleOverride(type, !overrideEnabled)}
              disabled={saving}
              className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-governate-500 focus:ring-offset-2 ${
                overrideEnabled
                  ? 'bg-governate-600'
                  : 'bg-gray-200 dark:bg-gray-700'
              } ${saving ? 'opacity-50 cursor-not-allowed' : ''}`}
              role="switch"
              aria-checked={overrideEnabled}
            >
              <span
                className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                  overrideEnabled ? 'translate-x-5' : 'translate-x-0'
                }`}
              />
            </button>
          </div>
        </div>

        {/* Display Items Section */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            {t('displayItems')}
          </label>
          <div className="space-y-3 border border-gray-200 dark:border-gray-700 rounded-lg p-3 bg-gray-50 dark:bg-gray-900">
            {/* Radio buttons for text mode */}
            <div className="space-y-2">
              <label className="flex items-center">
                <input
                  type="radio"
                  name={`text-mode-${type}`}
                  checked={!watermark.text_config.custom_text}
                  onChange={() => {
                    const updatedTextConfig = { 
                      ...watermark.text_config, 
                      custom_text: false,
                      custom_text_value: '' // Clear custom text value when switching to combination
                    };
                    if (type === 'screen') {
                      setScreenWatermark({ ...screenWatermark, text_config: updatedTextConfig });
                    } else {
                      setPrintWatermark({ ...printWatermark, text_config: updatedTextConfig });
                    }
                    setHasChanges(true);
                  }}
                  disabled={saving}
                  className="mr-2 text-governate-600 focus:ring-governate-500 disabled:opacity-50"
                />
                <span className="text-sm text-gray-700 dark:text-gray-300">{t('combination')}</span>
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  name={`text-mode-${type}`}
                  checked={watermark.text_config.custom_text || false}
                  onChange={() => {
                    const updatedTextConfig = {
                      ...watermark.text_config,
                      custom_text: true,
                      owner_company_name: false,
                      recipient_company_name: false,
                      owner_email: false,
                      recipient_email: false,
                      recipient_ip_address: false,
                      timestamp: false,
                      file_name: false,
                      device_name: false,
                    };
                    if (type === 'screen') {
                      setScreenWatermark({ ...screenWatermark, text_config: updatedTextConfig });
                    } else {
                      setPrintWatermark({ ...printWatermark, text_config: updatedTextConfig });
                    }
                    setHasChanges(true);
                  }}
                  disabled={saving}
                  className="mr-2 text-governate-600 focus:ring-governate-500 disabled:opacity-50"
                />
                <span className="text-sm text-gray-700 dark:text-gray-300">{t('customText')}</span>
              </label>
            </div>

            {/* Show checkboxes only when Combination is selected */}
            {!watermark.text_config.custom_text && (
              <div className="space-y-2 pt-2 border-t border-gray-200 dark:border-gray-700">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={watermark.text_config.owner_company_name}
                    onChange={(e) => handleTextConfigChange(type, 'owner_company_name', e.target.checked)}
                    disabled={saving}
                    className="mr-2 rounded border-gray-300 text-governate-600 focus:ring-governate-500 disabled:opacity-50"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300">{t('displayItemLabels.owner_company_name')}</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={watermark.text_config.recipient_company_name}
                    onChange={(e) => handleTextConfigChange(type, 'recipient_company_name', e.target.checked)}
                    disabled={saving}
                    className="mr-2 rounded border-gray-300 text-governate-600 focus:ring-governate-500 disabled:opacity-50"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300">{t('displayItemLabels.recipient_company_name')}</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={watermark.text_config.owner_email}
                    onChange={(e) => handleTextConfigChange(type, 'owner_email', e.target.checked)}
                    disabled={saving}
                    className="mr-2 rounded border-gray-300 text-governate-600 focus:ring-governate-500 disabled:opacity-50"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300">{t('displayItemLabels.owner_email')}</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={watermark.text_config.recipient_email}
                    onChange={(e) => handleTextConfigChange(type, 'recipient_email', e.target.checked)}
                    disabled={saving}
                    className="mr-2 rounded border-gray-300 text-governate-600 focus:ring-governate-500 disabled:opacity-50"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300">{t('displayItemLabels.recipient_email')}</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={watermark.text_config.recipient_ip_address}
                    onChange={(e) => handleTextConfigChange(type, 'recipient_ip_address', e.target.checked)}
                    disabled={saving}
                    className="mr-2 rounded border-gray-300 text-governate-600 focus:ring-governate-500 disabled:opacity-50"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300">{t('displayItemLabels.recipient_ip_address')}</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={watermark.text_config.timestamp}
                    onChange={(e) => handleTextConfigChange(type, 'timestamp', e.target.checked)}
                    disabled={saving}
                    className="mr-2 rounded border-gray-300 text-governate-600 focus:ring-governate-500 disabled:opacity-50"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300">{t('displayItemLabels.timestamp')}</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={watermark.text_config.file_name}
                    onChange={(e) => handleTextConfigChange(type, 'file_name', e.target.checked)}
                    disabled={saving}
                    className="mr-2 rounded border-gray-300 text-governate-600 focus:ring-governate-500 disabled:opacity-50"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300">{t('displayItemLabels.file_name')}</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={watermark.text_config.device_name}
                    onChange={(e) => handleTextConfigChange(type, 'device_name', e.target.checked)}
                    disabled={saving}
                    className="mr-2 rounded border-gray-300 text-governate-600 focus:ring-governate-500 disabled:opacity-50"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300">{t('displayItemLabels.device_name')}</span>
                </label>
              </div>
            )}

            {/* Show custom text input only when Custom Text is selected */}
            {watermark.text_config.custom_text && (
              <div className="pt-2 border-t border-gray-200 dark:border-gray-700">
                <input
                  type="text"
                  value={watermark.text_config.custom_text_value}
                  onChange={(e) => {
                    if (type === 'screen') {
                      setScreenWatermark({
                        ...screenWatermark,
                        text_config: { ...screenWatermark.text_config, custom_text_value: e.target.value }
                      });
                    } else {
                      setPrintWatermark({
                        ...printWatermark,
                        text_config: { ...printWatermark.text_config, custom_text_value: e.target.value }
                      });
                    }
                    setHasChanges(true);
                  }}
                  disabled={saving}
                  placeholder={t('customTextPlaceholder')}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-governate-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
                />
              </div>
            )}
          </div>
        </div>

        {/* First row: Font, Font Size */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t('font')}
            </label>
            <select
              value={watermark.font}
              onChange={(e) => {
                if (type === 'screen') {
                  setScreenWatermark({ ...screenWatermark, font: e.target.value });
                } else {
                  setPrintWatermark({ ...printWatermark, font: e.target.value });
                }
                setHasChanges(true);
              }}
              disabled={saving}
              className="inline-block min-w-[140px] max-w-[200px] px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-governate-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <option value="Arial">{t('fonts.Arial')}</option>
              <option value="Times New Roman">{t('fonts.Times New Roman')}</option>
              <option value="Courier New">{t('fonts.Courier New')}</option>
              <option value="Helvetica">{t('fonts.Helvetica')}</option>
              <option value="Verdana">{t('fonts.Verdana')}</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t('fontSize')}
            </label>
            <input
              type="number"
              value={watermark.font_size}
              onChange={(e) => {
                if (type === 'screen') {
                  setScreenWatermark({ ...screenWatermark, font_size: parseInt(e.target.value) || 12 });
                } else {
                  setPrintWatermark({ ...printWatermark, font_size: parseInt(e.target.value) || 12 });
                }
                setHasChanges(true);
              }}
              disabled={saving}
              min="8"
              max="72"
              className="w-24 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-governate-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
            />
          </div>
        </div>

        {/* Second row: Color, Opacity, Rotation Angle */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t('color')}
            </label>
            <div className="flex items-center space-x-2">
              <input
                type="color"
                value={watermark.color}
                onChange={(e) => {
                  if (type === 'screen') {
                    setScreenWatermark({ ...screenWatermark, color: e.target.value });
                  } else {
                    setPrintWatermark({ ...printWatermark, color: e.target.value });
                  }
                  setHasChanges(true);
                }}
                disabled={saving}
                className="h-10 w-20 border border-gray-300 dark:border-gray-600 rounded-lg cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
              />
              <input
                type="text"
                value={watermark.color}
                onChange={(e) => {
                  if (type === 'screen') {
                    setScreenWatermark({ ...screenWatermark, color: e.target.value });
                  } else {
                    setPrintWatermark({ ...printWatermark, color: e.target.value });
                  }
                  setHasChanges(true);
                }}
                disabled={saving}
                placeholder="#000000"
                className="w-32 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-governate-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t('opacity', { value: watermark.opacity })}
            </label>
            <input
              type="range"
              value={watermark.opacity}
              onChange={(e) => {
                if (type === 'screen') {
                  setScreenWatermark({ ...screenWatermark, opacity: parseFloat(e.target.value) });
                } else {
                  setPrintWatermark({ ...printWatermark, opacity: parseFloat(e.target.value) });
                }
                setHasChanges(true);
              }}
              disabled={saving}
              min="0"
              max="1"
              step="0.1"
              className="w-full max-w-[200px] h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t('rotationAngle')}
            </label>
            <AlignmentControl
              value={(() => {
                if (typeof watermark.angle === 'number') {
                  // Normalize to only 45 or -45
                  if (watermark.angle === -45) return -45;
                  return 45; // Default to diagonal
                }
                return 45; // Default to diagonal
              })()}
              onChange={(degrees) => {
                // Ensure only 45 or -45 are set
                const normalizedAngle = degrees === -45 ? -45 : 45;
                if (type === 'screen') {
                  setScreenWatermark({ ...screenWatermark, angle: normalizedAngle });
                } else {
                  setPrintWatermark({ ...printWatermark, angle: normalizedAngle });
                }
                setHasChanges(true);
              }}
              disabled={saving}
            />
          </div>
        </div>
      </div>
    );
  };


  return (
    <div className="space-y-6">
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <p className="text-sm text-red-800 dark:text-red-200">{error}</p>
        </div>
      )}

      {/* Tab Navigation */}
      <div className="bg-white dark:bg-black shadow overflow-hidden sm:rounded-lg border border-gray-200 dark:border-gray-900 transition-colors">
        <div className="border-b border-gray-200 dark:border-gray-800">
          <nav className="flex -mb-px">
            <button
              onClick={() => setActiveTab('screen')}
              className={`py-4 px-6 border-b-2 font-medium text-sm flex items-center space-x-2 ${
                activeTab === 'screen'
                  ? 'border-governate-500 text-governate-600 dark:text-governate-400'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-200'
              }`}
            >
              <EyeIcon className="w-5 h-5" />
              <span>{t('tabs.screenWatermark')}</span>
            </button>
            <button
              onClick={() => setActiveTab('print')}
              className={`py-4 px-6 border-b-2 font-medium text-sm flex items-center space-x-2 ${
                activeTab === 'print'
                  ? 'border-governate-500 text-governate-600 dark:text-governate-400'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-200'
              }`}
            >
              <PrinterIcon className="w-5 h-5" />
              <span>{t('tabs.printWatermark')}</span>
            </button>
          </nav>
        </div>

        <div className="px-4 py-5 sm:px-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-white">
                {activeTab === 'screen' ? t('screen.title') : t('print.title')}
              </h3>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                {activeTab === 'screen' ? t('screen.description') : t('print.description')}
              </p>
            </div>
          </div>

          <div className="border-t border-gray-200 dark:border-gray-800 pt-5">
            {renderWatermarkForm(activeTab)}
          </div>
        </div>
      </div>

      {/* Preview Modal */}
      <WatermarkPreview
        isOpen={showPreview}
        onClose={() => setShowPreview(false)}
        watermark={activeTab === 'screen' ? screenWatermark : printWatermark}
        type={activeTab}
        onWatermarkChange={handleWatermarkChange}
      />
    </div>
  );
};

export default AdminWatermarkSettings;

